import { ref } from 'vue';

// Per-app toast queue (unified notices rollout — see Documents/lumi/ux/notices).
// The lib owns the look (NoticeToast/NoticeToastHost); this composable owns the
// queue + canonical timings. Standardized signature across all apps:
//   toast.success(message, { title? })
//   toast.info(message, { title? })
//   toast.warning(message, { title?, action?, actionLabel? })
//   toast.error(message, { title?, retry? })   // sticky; retry renders the action slot
export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: number;
  variant: ToastVariant;
  message: string;
  title?: string;
  /** error only — renders the retry action (self-healing). */
  retry?: () => void;
  /** warning only — optional action. */
  action?: () => void;
  actionLabel?: string;
  /**
   * `performance.now()` at push (#470). ToastContainer's page-wide "click
   * outside dismisses everything" listener uses this to never dismiss the
   * toast the very same pointer event just created — comparable to a
   * PointerEvent's own `timeStamp`, which Date.now() is not.
   */
  createdAt: number;
}

interface ToastOptions {
  title?: string;
  retry?: () => void;
  action?: () => void;
  actionLabel?: string;
}

// Canonical timings (#470 — the old 4.5s/7s durations read as too sticky):
// success/info 3s, warning 5s, error still sticky (manual dismiss) so a
// failure the user must see can't vanish on its own. A click anywhere on the
// page outside a toast dismisses every visible one regardless of variant
// (NoticeToastHost.vue) — errors close that way even though they never
// auto-close on a timer.
const DURATION: Record<ToastVariant, number> = {
  success: 3000,
  info: 3000,
  warning: 5000,
  error: 0,
};

const toasts = ref<Toast[]>([]);
let nextId = 0;

export function useToast() {
  function push(variant: ToastVariant, message: string, opts: ToastOptions = {}): number {
    // De-dupe: when several requests fail at once (e.g. parallel bootstrap
    // loads), don't stack identical copies — surface the same message once.
    const existing = toasts.value.find(
      (t) => t.variant === variant && t.message === message && t.title === opts.title,
    );
    if (existing) return existing.id;
    const id = nextId++;
    const createdAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    toasts.value.push({ id, variant, message, createdAt, ...opts });
    const duration = DURATION[variant];
    if (duration > 0) {
      setTimeout(() => dismiss(id), duration);
    }
    return id;
  }

  function dismiss(id: number) {
    const idx = toasts.value.findIndex((t) => t.id === id);
    if (idx !== -1) toasts.value.splice(idx, 1);
  }

  function success(message: string, opts?: ToastOptions) { return push('success', message, opts); }
  function info(message: string, opts?: ToastOptions) { return push('info', message, opts); }
  function warning(message: string, opts?: ToastOptions) { return push('warning', message, opts); }
  function error(message: string, opts?: ToastOptions) { return push('error', message, opts); }

  return { toasts, dismiss, success, error, warning, info };
}
